import { create } from 'zustand';
import { ObjectService } from '../api/ObjectService';
import { InferenceService } from '../api/InferenceService';
import { splitPDF, chunksToFiles } from '../components/documents/SplitPDF';

export interface UploadStatus {
  progress: string;
  error?: string;
  isComplete: boolean;
}

export interface UploadJob {
  id: string;
  files: File[];
  projectId: string;
  status: Record<string, UploadStatus>;
  createdAt: number;
}

export interface UploadStore {
  // State
  activeUploads: UploadJob[];

  // Actions
  startUpload: (files: File[], projectId: string) => Promise<string>;
  getUploadStatus: (jobId: string) => UploadJob | undefined;
  clearCompletedUploads: () => void;

  // Background processing
  processFilesAndUpload: (files: File[], projectId: string, jobId: string) => Promise<void>;
}

let uploadCounter = 0;

export const useUploadStore = create<UploadStore>((set, get) => ({
  activeUploads: [],

  startUpload: async (files: File[], projectId: string) => {
    const jobId = `upload-${Date.now()}-${uploadCounter++}`;

    // Create initial job status
    const newJob: UploadJob = {
      id: jobId,
      files,
      projectId,
      status: files.reduce(
        (acc, file) => {
          acc[file.name] = { progress: 'Preparing...', isComplete: false };
          return acc;
        },
        {} as Record<string, UploadStatus>
      ),
      createdAt: Date.now(),
    };

    // Add job to store
    set(state => ({
      activeUploads: [...state.activeUploads, newJob],
    }));

    // Start processing in the background
    // We don't await this as we want it to run in the background
    get().processFilesAndUpload(files, projectId, jobId);

    return jobId;
  },

  getUploadStatus: (jobId: string) => {
    return get().activeUploads.find(job => job.id === jobId);
  },

  clearCompletedUploads: () => {
    set(state => ({
      activeUploads: state.activeUploads.filter(
        job => !Object.values(job.status).every(status => status.isComplete)
      ),
    }));
  },

  processFilesAndUpload: async (files: File[], projectId: string, jobId: string) => {
    // Process PDFs - Split large PDFs if needed
    const processedFiles: File[] = [];

    // Update job status to processing
    const updateFileStatus = (fileName: string, status: Partial<UploadStatus>) => {
      set(state => {
        const jobIndex = state.activeUploads.findIndex(job => job.id === jobId);
        if (jobIndex === -1) return state;

        const newUploads = [...state.activeUploads];
        const currentStatus = newUploads[jobIndex].status[fileName] || {
          progress: '',
          isComplete: false,
        };

        newUploads[jobIndex].status[fileName] = {
          ...currentStatus,
          ...status,
        };

        return { activeUploads: newUploads };
      });
    };

    // Process PDF files - split large ones if needed
    for (const file of files) {
      if (file.type === 'application/pdf') {
        try {
          updateFileStatus(file.name, { progress: 'Processing PDF...' });

          // Check if we need to split the PDF
          if (file.size > 10 * 1024 * 1024) {
            // 10MB threshold
            // Split the PDF into smaller chunks
            const splitResult = await splitPDF(file);
            const splitFiles = chunksToFiles(splitResult, file.name);

            processedFiles.push(...splitFiles);

            // Update status for original file
            updateFileStatus(file.name, {
              progress: `Split into ${splitFiles.length} parts. Uploading...`,
            });

            // Add status entries for the new split files
            splitFiles.forEach(splitFile => {
              updateFileStatus(splitFile.name, { progress: 'Queued for upload...' });
            });
          } else {
            processedFiles.push(file);
            updateFileStatus(file.name, { progress: 'Queued for upload...' });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to process PDF';
          updateFileStatus(file.name, {
            progress: 'Error processing PDF',
            error: errorMessage,
            isComplete: true,
          });
        }
      } else {
        // Non-PDF files
        processedFiles.push(file);
        updateFileStatus(file.name, { progress: 'Queued for upload...' });
      }
    }

    // Upload files
    for (const file of processedFiles) {
      try {
        updateFileStatus(file.name, { progress: 'Uploading...' });

        // Upload the file to GCS
        const response = await ObjectService.handlePUTGCSRequest(file, projectId);

        updateFileStatus(file.name, { progress: 'Processing on server...' });

        // Process the uploaded image
        await InferenceService.processImage(response);

        // Mark as complete
        updateFileStatus(file.name, {
          progress: 'Upload complete. The file will appear in your documents shortly.',
          isComplete: true,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        updateFileStatus(file.name, {
          progress: 'Error',
          error: errorMessage,
          isComplete: true,
        });
      }
    }
  },
}));
